import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, withModifiers as _withModifiers, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "flex flex-col gap-5 md:max-w-[624px] px-5 md:px-10 py-5 bg-white border border-neutral-500 rounded-lg" }
const _hoisted_2 = { class: "flex flex-col md:flex-row items-baseline gap-1 md:gap-5 pointer-events-none" }
const _hoisted_3 = {
  class: "cursor-pointer md:min-w-[170px]",
  for: "login"
}
const _hoisted_4 = { class: "flex flex-col md:flex-row items-baseline gap-1 md:gap-5" }
const _hoisted_5 = {
  class: "cursor-pointer md:min-w-[170px]",
  for: "enable_category_creation"
}
const _hoisted_6 = { class: "flex flex-col md:flex-row items-baseline gap-1 md:gap-5" }
const _hoisted_7 = {
  class: "cursor-pointer md:min-w-[170px]",
  for: "category_creation_limit"
}
const _hoisted_8 = { class: "flex flex-col md:flex-row items-baseline gap-1 md:gap-5" }
const _hoisted_9 = {
  class: "cursor-pointer md:min-w-[170px]",
  for: "rest_url"
}

import TextInput from '../form/TextInput.vue';
import Button from '../base/Button.vue';
import { inject, ref } from 'vue';
import { AppConfigType } from '~src/admin/types';
import Axios from '~src/shared/axios';
import { useNotificationStore } from '~src/admin/stores/useNotificationStore';
import { useGettext } from 'vue3-gettext';
import Toggle from '../form/Toggle.vue';

// Translations

export default /*@__PURE__*/_defineComponent({
  __name: 'MyAccount',
  setup(__props) {

const { $gettext } = useGettext();

// Get plugin config
const config: AppConfigType = inject('pluginConfig');

// Show notification
const { addNotification } = useNotificationStore();

// Input values
const loginValue = ref(config.settings.email);
const restUrl = ref(config.settings.rest_url);
const enableCategoryCreation = ref(config.settings.enable_category_creation);
const categoryCreationLimit = ref(config.settings.category_creation_limit);

// Handle errors
const errorRestURL = ref('');
const errorCategoryCreationLimit = ref('');

// Loading
const loading = ref(false);

const updateSettings = async () => {
    try {
        // Reset errors
        errorRestURL.value = '';

        // Set loading
        loading.value = true;

        // Validate the url
        if (restUrl.value.length > 0 && !restUrl.value.startsWith('http://') && !restUrl.value.startsWith('https://')) {
            errorRestURL.value = $gettext('Please enter a valid URL!');
            return;
        }

        // Validate the category creation limit
        if (categoryCreationLimit.value < 1) {
            errorCategoryCreationLimit.value = $gettext('Category creation limit must be greater than 0!');
            return;
        }

        // Prepare the body
        const body = {
            rest_url: restUrl.value,
            enable_category_creation: enableCategoryCreation.value,
            category_creation_limit: categoryCreationLimit.value,
        };

        // Call the REST API to save settings
        const response = await Axios.post(`${config.rest.endpoints.settings}/update_plugin`, body);

        // Check if the response is successful
        if (response.status !== 200 && !response.data.success) {
            throw new Error(`REST API error: ${response.status} - ${response.statusText}`);
        }

        // Show message
        addNotification({
            message: $gettext('Settings updated succesfully!'),
            type: 'success',
        });
    } catch (error) {
        // Show error
        console.error('Error fetching posts:', error);

        addNotification({
            message: $gettext('There was an error while updating the settings!'),
            type: 'danger',
        });
    } finally {
        // Set loading
        loading.value = false;
    }
};

// Account expired
const isAccountExpired = inject<boolean>('isAccountExpired');

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("form", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("label", _hoisted_3, _toDisplayString(_unref($gettext)('Login:')), 1),
      _createVNode(TextInput, {
        disabled: true,
        name: "login",
        modelValue: loginValue.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((loginValue).value = $event))
      }, null, 8, ["modelValue"])
    ]),
    _createElementVNode("div", _hoisted_4, [
      _createElementVNode("label", _hoisted_5, _toDisplayString(_unref($gettext)('Enable category creation:')), 1),
      _createVNode(Toggle, {
        disabled: loading.value || _unref(isAccountExpired),
        modelValue: enableCategoryCreation.value,
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((enableCategoryCreation).value = $event)),
        "active-text": _unref($gettext)('Yes'),
        "inactive-text": _unref($gettext)('No')
      }, null, 8, ["disabled", "modelValue", "active-text", "inactive-text"])
    ]),
    _createElementVNode("div", _hoisted_6, [
      _createElementVNode("label", _hoisted_7, _toDisplayString(_unref($gettext)('Category creation limit:')), 1),
      _createVNode(TextInput, {
        disabled: loading.value || _unref(isAccountExpired),
        type: "number",
        name: "category_creation_limit",
        modelValue: categoryCreationLimit.value,
        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((categoryCreationLimit).value = $event)),
        "error-msg": errorCategoryCreationLimit.value
      }, null, 8, ["disabled", "modelValue", "error-msg"])
    ]),
    _createElementVNode("div", _hoisted_8, [
      _createElementVNode("label", _hoisted_9, _toDisplayString(_unref($gettext)('Rest URL:')), 1),
      _createVNode(TextInput, {
        disabled: loading.value || _unref(isAccountExpired),
        type: "url",
        name: "rest_url",
        "error-msg": errorRestURL.value,
        modelValue: restUrl.value,
        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((restUrl).value = $event))
      }, null, 8, ["disabled", "error-msg", "modelValue"])
    ]),
    _createVNode(Button, {
      class: "max-md:w-full self-end",
      type: "primary",
      text: _unref($gettext)('Save changes'),
      size: "large",
      disabled: !restUrl.value.length || _unref(isAccountExpired),
      loading: loading.value,
      onClick: _withModifiers(updateSettings, ["prevent"])
    }, null, 8, ["text", "disabled", "loading"])
  ]))
}
}

})